import Phaser from 'phaser';

// Game configuration
export const GameConfig: Phaser.Types.Core.GameConfig = {
  type: Phaser.AUTO,
  parent: 'game-container',
  width: 360,
  height: 640,
  backgroundColor: '#1a4e6e',
  scale: {
    mode: Phaser.Scale.RESIZE,
    autoCenter: Phaser.Scale.CENTER_BOTH,
    width: '100%',
    height: '100%'
  },
  physics: {
    default: 'arcade',
    arcade: {
      gravity: { x: 0, y: 0 },
      debug: false
    }
  },
  render: {
    pixelArt: false,
    antialias: true,
    roundPixels: true
  },
  // Disable default audio context until user interaction
  audio: {
    disableWebAudio: false,
    noAudio: false
  }
};

// Game constants
export const GAME_CONSTANTS = {
  // Oxygen system
  MAX_OXYGEN: 100,
  OXYGEN_DECREASE_RATE: 0.15,
  OXYGEN_DEPTH_PENALTY_FACTOR: 0.08,
  OXYGEN_DEPTH_THRESHOLD: 1000,
  OXYGEN_GAIN_PER_BUBBLE: 15,
  OXYGEN_COST_MIN: 8,
  OXYGEN_COST_MAX: 25,
  
  // Depth system
  DEPTH_DIVISOR: 10, // Divide diver Y position by this to get depth
  DEPTH_MILESTONES: [100, 200, 300, 400, 500],
  
  // Physics
  BASE_UPWARD_FORCE: 0.8,
  UPWARD_FORCE_MULTIPLIER: 5,
  DRAG_FACTOR: 0.98,
  DRAG_THRESHOLD: 50,
  MAX_VELOCITY: 300,
  ROTATION_THRESHOLD: 20,
  ROTATION_SPEED: 0.1,
  BUOYANCY_TOP_THRESHOLD: 50,
  BUOYANCY_BOTTOM_THRESHOLD: 20,
  BUOYANCY_DOWNWARD_FORCE: 5,
  
  // Diver
  DIVER_COLLISION_RADIUS: 5,
  DIVER_HITBOX_SIZE: 4,
  DIVER_SCALE: 0.5,
  DIVER_ORIGIN_X: 0.5,
  DIVER_ORIGIN_Y: 0.3,
  
  // Animation
  BASE_ANIMATION_SPEED: 0.5,
  BURST_ANIMATION_SPEED: 1.0,
  MIN_ANIMATION_SPEED: 0.1,
  ANIMATION_FRAME_DURATION: 100,
  
  // Movement
  BASE_MOVEMENT_SPEED: 200,
  MOVEMENT_SPEED_BONUS: 250,
  MOVEMENT_DISTANCE_THRESHOLD: 5,
  BUBBLE_COLLECTION_ACCELERATION: 300,
  BUBBLE_COLLECTION_DEPTH_BONUS: 200,
  BUBBLE_COLLECTION_DEPTH_FACTOR: 500,
  
  // Camera
  CAMERA_FOLLOW_LERP_X: 0.1,
  CAMERA_FOLLOW_LERP_Y: 0.1,
  CAMERA_DEADZONE_X: 20,
  CAMERA_DEADZONE_Y: 200,
  CAMERA_ZOOM_DURATION: 150,
  CAMERA_ZOOM_SCALE: 1.1,
  
  // World
  WORLD_HEIGHT: 10000,
  BACKGROUND_EXTENSION_THRESHOLD: 200,
  BACKGROUND_EXTENSION_AMOUNT: 400,
  
  // UI
  UI_UPDATE_INTERVAL: 3, // Update UI every N frames
  UI_UPDATE_THRESHOLD: 0.5, // Minimum change to trigger UI update
} as const;

// Depth zones configuration
export interface DepthZone {
  depth: number;
  color: number;
  name: string;
}

export const DEPTH_ZONES: DepthZone[] = [
  { depth: 100, color: 0x174563, name: 'Shallow Waters' },
  { depth: 300, color: 0x143d58, name: 'Mid Waters' },
  { depth: 600, color: 0x113450, name: 'Deep Waters' },
  { depth: 1000, color: 0x0e2b45, name: 'Abyssal Zone' },
  { depth: 2000, color: 0x0a1f35, name: 'Hadal Zone' }
];

// UI colors
export const UI_COLORS = {
  background: 0x0a2a3f,
  surface: 0x1a4e6e,
  midWater: 0x143d58,
  deep: 0x0e2b45,
  abyss: 0x0a1f35,
  accent: 0x00ccff,
  white: 0xffffff,
  red: 0xff3b30,
  hudBg: 0x000000,
  oxygenHigh: '#ffffff',
  oxygenMedium: '#ffcc00',
  oxygenLow: '#ff3b30',
} as const;

// Audio settings
export const AUDIO_SETTINGS = {
  BACKGROUND_MUSIC_VOLUME: 0.3,
  BUBBLE_SOUND_VOLUME: 0.4,
  SUCCESS_VOICE_VOLUME: 0.6,
  DEEPER_VOICE_VOLUME: 0.6,
  MILESTONE_VOICE_VOLUME: 0.7,
  FAILURE_VOICE_VOLUME: 0.7,
  INTRO_VOICE_VOLUME: 0.7,
  READY_VOICE_VOLUME: 0.7,
  SPLASH_SOUND_VOLUME: 0.5,
} as const;

// Bubble configuration
export const BUBBLE_CONFIG = {
  INITIAL_COUNT: 15,
  MIN_ON_SCREEN: 10,
  GENERATION_INTERVAL: 1000,
  GENERATION_COUNT_LOW: 2,
  GENERATION_COUNT_HIGH: 5,
  COLLISION_CHECK_INTERVAL: 100,
  COLLISION_RADIUS_FACTOR: 0.5,
  BASE_SIZE: 50,
  SCALE_MIN: 0.6,
  SCALE_MAX: 1.8,
  BASE_SPEED_MIN: 70,
  BASE_SPEED_MAX: 120,
  SPEED_ADJUSTMENT_FACTOR: 0.2,
  LIFETIME: 15000,
  MIN_DISTANCE_FROM_DIVER: 100,
  CLEANUP_MARGIN: 200,
} as const;

// Parallax configuration
export const PARALLAX_CONFIG = {
  INITIAL_DISTANT_COUNT: 5,
  INITIAL_MID_COUNT: 5,
  INITIAL_CLOSE_COUNT: 5,
  GENERATION_INTERVAL: 1500,
  GENERATION_DEPTH_THRESHOLD: 20,
  DISTANT_PARALLAX_MIN: 0.1,
  DISTANT_PARALLAX_MAX: 0.3,
  DISTANT_ALPHA_MIN: 0.6,
  DISTANT_ALPHA_MAX: 0.8,
  MID_PARALLAX_MIN: 0.3,
  MID_PARALLAX_MAX: 0.6,
  MID_ALPHA_MIN: 0.7,
  MID_ALPHA_MAX: 0.9,
  CLOSE_PARALLAX_MIN: 0.6,
  CLOSE_PARALLAX_MAX: 0.9,
  CLOSE_ALPHA_MIN: 0.8,
  CLOSE_ALPHA_MAX: 1.0,
  PARALLAX_MULTIPLIER: 2.5,
  VERTICAL_PARALLAX_THRESHOLD: 30,
  VERTICAL_PARALLAX_DIVISOR: 200,
  VERTICAL_PARALLAX_MULTIPLIER: 3,
  UPDATE_MARGIN: 200,
  CLEANUP_MARGIN: 500,
  CLEANUP_DELAY: 10000,
  CLEANUP_DISTANCE: 300,
} as const; 